// Verificación en dos pasos (MFA/TOTP) del propio usuario — Mi cuenta.
// Solo aparece en instalaciones cuya BD la tiene (TrackConstruccion, script 006):
// si /rpc/mfa_estado no existe (404) la tarjeta no se muestra.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Copy, Download, Loader2, ShieldCheck } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { agruparSecreto, descargarCodigos } from '@/components/auth/MfaPaso';
import { useTranslation } from '@/lib/hooks/useTranslation';
import { fmtFechaHora } from '@/lib/utils/regional';

interface Estado { activo: boolean; activadoEn: string | null; requerido: boolean; codigosRestantes: number }
type Accion = null | 'alta' | 'regenerar' | 'desactivar';

const inputCls = 'w-40 rounded-md border border-input bg-background px-3 py-2 text-center text-sm tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-ring';
const rpc = async <T,>(fn: string, body: object = {}) => (await apiClient.post(`/rpc/${fn}`, body)).data as T;

export function MfaCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [accion, setAccion] = useState<Accion>(null);
  const [alta, setAlta] = useState<{ secreto: string; uri: string } | null>(null);
  const [codigo, setCodigo] = useState('');
  const [codigos, setCodigos] = useState<string[] | null>(null);

  const { data: estado, isError } = useQuery<Estado>({
    queryKey: ['mfa-estado'],
    queryFn: () => rpc<Estado>('mfa_estado'),
    retry: false,
  });

  const cerrar = () => { setAccion(null); setAlta(null); setCodigo(''); };
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['mfa-estado'] });

  const iniciar = useMutation({
    mutationFn: () => rpc<{ secreto: string; uri: string }>('mfa_iniciar'),
    onSuccess: (r) => { setAlta(r); setAccion('alta'); setCodigos(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const confirmar = useMutation({
    mutationFn: () => {
      const fn = accion === 'alta' ? 'mfa_confirmar' : accion === 'regenerar' ? 'mfa_regenerar_codigos' : 'mfa_desactivar';
      return rpc<{ error?: string; codigosRecuperacion?: string[]; ok?: boolean }>(fn, { _codigo: codigo });
    },
    onSuccess: (r) => {
      if (r.error) { toast.error(t('mfa.invalidoSimple')); setCodigo(''); return; }
      if (r.codigosRecuperacion) setCodigos(r.codigosRecuperacion);
      toast.success(accion === 'desactivar' ? t('mfa.desactivado') : accion === 'alta' ? t('mfa.activado') : t('mfa.regenerados'));
      cerrar(); refrescar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isError || !estado) return null;

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <ShieldCheck className="h-5 w-5 text-muted-foreground" />
        <CardTitle className="text-base">{t('mfa.tarjetaTitulo')}</CardTitle>
      </CardHeader>
      <CardContent className="max-w-xl space-y-3 text-sm">
        <p className="text-muted-foreground">{t('mfa.tarjetaAyuda')}</p>

        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${estado.activo ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'}`}>
            {estado.activo ? t('mfa.estadoActivo') : t('mfa.estadoInactivo')}
          </span>
          {estado.activo && estado.activadoEn && <span className="text-xs text-muted-foreground">{t('mfa.desde', { fecha: fmtFechaHora(estado.activadoEn) })}</span>}
          {estado.requerido && <span className="text-xs text-muted-foreground">· {t('mfa.exigido')}</span>}
        </div>
        {estado.activo && (
          <p className={`text-xs ${estado.codigosRestantes <= 3 ? 'text-amber-600' : 'text-muted-foreground'}`}>
            {t('mfa.restantes', { n: estado.codigosRestantes })}
          </p>
        )}

        {codigos && (
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <p className="mb-2 text-xs font-medium">{t('mfa.codigosAyuda')}</p>
            <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-[13px]">{codigos.map((c) => <span key={c}>{c}</span>)}</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => void navigator.clipboard?.writeText(codigos.join('\n'))}><Copy className="mr-1.5 h-3.5 w-3.5" />{t('mfa.copiar')}</Button>
              <Button size="sm" variant="outline" onClick={() => descargarCodigos(codigos)}><Download className="mr-1.5 h-3.5 w-3.5" />{t('mfa.descargar')}</Button>
              <Button size="sm" onClick={() => setCodigos(null)}>{t('mfa.guardados')}</Button>
            </div>
          </div>
        )}

        {accion === null && !codigos && (
          <div className="flex flex-wrap gap-2">
            {!estado.activo && (
              <Button size="sm" onClick={() => iniciar.mutate()} disabled={iniciar.isPending}>
                {iniciar.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('mfa.configurar')}
              </Button>
            )}
            {estado.activo && <Button size="sm" variant="outline" onClick={() => setAccion('regenerar')}>{t('mfa.regenerar')}</Button>}
            {estado.activo && !estado.requerido && <Button size="sm" variant="outline" onClick={() => setAccion('desactivar')}>{t('mfa.desactivar')}</Button>}
          </div>
        )}

        {accion && (
          <div className="space-y-3 rounded-md border border-border p-3">
            {accion === 'alta' && alta && (
              <>
                <p className="text-xs text-muted-foreground">{t('mfa.enrolarAyuda')}</p>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="rounded bg-white p-2"><QRCodeSVG value={alta.uri} size={140} /></div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('mfa.claveManual')}</p>
                    <code className="select-all break-all text-xs">{agruparSecreto(alta.secreto)}</code>
                  </div>
                </div>
              </>
            )}
            <p className="text-xs text-muted-foreground">{accion === 'alta' ? t('mfa.codigoAlta') : t('mfa.codigoConfirmar')}</p>
            <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (codigo.trim()) confirmar.mutate(); }}>
              <input className={inputCls} value={codigo} onChange={(e) => setCodigo(e.target.value)} autoFocus
                autoComplete="one-time-code" maxLength={11} placeholder="000000" />
              <Button size="sm" type="submit" variant={accion === 'desactivar' ? 'destructive' : 'default'} disabled={confirmar.isPending || !codigo.trim()}>
                {confirmar.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {accion === 'alta' ? t('mfa.activar') : accion === 'regenerar' ? t('mfa.regenerar') : t('mfa.desactivar')}
              </Button>
              <Button size="sm" type="button" variant="ghost" onClick={cerrar}>{t('mfa.cancelar')}</Button>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
